import React, { useEffect, useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePlansApi } from '../../../../common/hooks/use-plans-api.hook';
import { useVendorPlan } from '../../../../common/hooks/use-vendor-plan.hook';
import { useBillingApi } from '../../../../common/hooks/use-billing-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { UsageMeter } from '../../../../components/UsageMeter';

// Ordered labels for the numeric limits that getMyPlan reports usage for.
const LIMIT_LABELS: Record<string, string> = {
  maxProducts: 'Products',
  maxSpaces: 'Spaces',
  maxEmailsPerMonth: 'Emails / month',
  maxSyncFlowsPerDay: 'Sync flows / day',
  maxAutomationRules: 'Automation rules',
  maxLeads: 'Leads',
  maxImportProducts: 'Imports / month',
  monthlyCredits: 'Credits / month',
  maxSocialAccounts: 'Social accounts',
  maxScheduledPostsPerMonth: 'Scheduled posts / month',
  maxVisualizationsPerMonth: 'Visualizations / month',
  maxSeats: 'Seats',
  // The free plan's monthly meters (docs/plg-free-plan.md). Listed only when the
  // API reports usage for them, so an older API shows exactly what it did.
  maxRoomAnalysesPerMonth: 'Room analyses / month',
  maxShopperChatsPerMonth: 'Shopper chats / month',
  maxSeamlessPerMonth: 'Seamless textures / month',
  max3dModelsPerMonth: '3D models / month',
  maxBrandReadsPerMonth: 'Brand reads / month',
  maxMarketRefreshesPerMonth: 'Search analysis refreshes / month',
};

const FEATURE_LABELS: Record<string, string> = {
  apiIntegration: 'API Integration',
  emailAutomation: 'Email Automation',
  aiSpaceAnalysis: 'AI Space Analysis',
  seamlessGeneration: 'Seamless Generation',
  model3dGeneration: '3D Model Generation',
  advancedAnalytics: 'Advanced Analytics',
  customFields: 'Custom Fields',
  categoryManagement: 'Categories',
  productCardCustomization: 'Product Card Customization',
  contentStudio: 'Content Studio',
  socialScheduler: 'Social Scheduler',
  visualizationQuality: 'Visualization Quality',
};

interface MyPlanResponse {
  plan: { name: string; slug: string; limits: Record<string, any>; features: Record<string, any> };
  usage: Record<string, { current: number; limit: number }>;
}

const formatFeatureValue = (v: any): string => {
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  return String(v);
};

/**
 * Vendor-facing card showing the EFFECTIVE plan limits (with per-vendor
 * overrides already merged by the backend) plus current usage. Sourced from
 * GET /services/pms/plans/my-plan, so any super-admin override is reflected here.
 */
export const VendorPlanUsageCard: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const overridesLabel = (n: number): string =>
    t(n > 1 ? 'BILLING.OVERRIDE_OTHER' : 'BILLING.OVERRIDE_ONE', { n, defaultValue: `${n} custom override${n > 1 ? 's' : ''}` });
  const plansApi = usePlansApi();
  const contentStudioApi = useContentStudioApi();
  const { overrides, hasActiveSubscription, subscriptionStatus, subscriptionCurrentPeriodEnd } = useVendorPlan();
  const billingApi = useBillingApi();
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await billingApi.createPortalSession();
      window.location.href = url;
    } catch {
      setPortalLoading(false);
    }
  };

  const [data, setData] = useState<MyPlanResponse | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    plansApi
      .getMyPlan()
      .then((res) => { if (active) setData(res as MyPlanResponse); })
      .catch(() => { /* leave card hidden on failure */ })
      .finally(() => { if (active) setLoading(false); });
    // Real Content Studio credit balance (wallet), not the monthly grant amount.
    contentStudioApi
      .getCreditBalance()
      .then((res) => { if (active) setCreditBalance(res?.balance ?? null); })
      .catch(() => { /* balance row simply hidden on failure */ });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardClass = embedded ? 'border-t p-6' : 'mb-6 border bg-card p-6 md:mb-8';

  if (loading) {
    return (
      <div className={cn(cardClass, 'flex justify-center')} data-testid="usage-card-loading">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data?.usage) return null;

  const limitOverrideKeys = new Set(Object.keys(overrides?.limits || {}));
  const featureOverrideKeys = Object.keys(overrides?.features || {});
  const overrideCount = limitOverrideKeys.size + featureOverrideKeys.length;

  // monthlyCredits is a monthly *grant amount*, not a usage counter — it gets its
  // own row showing the real wallet balance, so exclude it from the usage meters.
  const monthlyGrant: number | undefined = data.plan?.limits?.monthlyCredits;
  const creditsOverridden = limitOverrideKeys.has('monthlyCredits');
  const showCredits = creditBalance !== null || (monthlyGrant !== undefined && monthlyGrant !== 0);

  const rows = Object.keys(LIMIT_LABELS)
    .filter((key) => key !== 'monthlyCredits' && data.usage[key])
    .map((key) => ({
      key,
      label:
        t(`QUOTE.VOLUME.${key}.LABEL`, { defaultValue: t(`FREE_PLAN.LIMITS.${key}`, LIMIT_LABELS[key]) }) +
        (limitOverrideKeys.has(key) ? ` (${t('BILLING.CUSTOM', 'custom')})` : ''),
      current: data.usage[key].current,
      limit: data.usage[key].limit,
    }));

  const formatGrant = (v: number | undefined): string => {
    if (v === undefined || v === null) return '—';
    if (v === -1) return t('BILLING.UNLIMITED', 'unlimited');
    return String(v);
  };

  return (
    <div className={cardClass} data-testid="usage-card">
      {!embedded && (
        <div className="mb-4 flex items-center gap-3">
          <h2 className="font-heading text-lg font-medium tracking-tight">
            {t('BILLING.YOUR_PLAN', 'Your plan')} — {data.plan?.name}
          </h2>
          {overrideCount > 0 && <Badge>{overridesLabel(overrideCount)}</Badge>}
          {hasActiveSubscription && (
            <Button variant="outline" size="sm" className="ms-auto" onClick={openPortal} disabled={portalLoading}>
              {portalLoading ? '…' : t('BILLING.MANAGE_SUBSCRIPTION', 'Manage subscription')}
            </Button>
          )}
        </div>
      )}

      {subscriptionStatus === 'past_due' && (
        <Alert className="mb-4">
          <TriangleAlert className="text-warning" />
          <AlertDescription>
            {t('BILLING.PAST_DUE', 'Your last payment failed. Update your payment method to keep your subscription active.')}
          </AlertDescription>
        </Alert>
      )}
      {!embedded && hasActiveSubscription && subscriptionCurrentPeriodEnd && (
        <p className="mb-4 text-sm text-muted-foreground">
          {t('BILLING.RENEWS_ON', 'Renews on {{date}}', { date: new Date(subscriptionCurrentPeriodEnd).toLocaleDateString() })}
        </p>
      )}

      <div className="mb-3.5 flex items-center gap-3">
        <p className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
          {t('BILLING.USAGE_THIS_PERIOD', 'Usage this period')}
        </p>
        {embedded && overrideCount > 0 && <Badge>{overridesLabel(overrideCount)}</Badge>}
      </div>
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => (
          <UsageMeter key={row.key} label={row.label} current={row.current} limit={row.limit} size="sm" />
        ))}
      </div>

      {showCredits && (
        <div data-testid="credits-row" className="mt-4 flex flex-wrap items-baseline gap-2 border-t pt-4">
          <span className="text-sm font-medium text-muted-foreground">
            {t('BILLING.CS_CREDITS', 'Content Studio credits')}{creditsOverridden ? ` (${t('BILLING.CUSTOM', 'custom')})` : ''}
          </span>
          {creditBalance !== null && (
            <span className="text-sm font-medium">
              {creditBalance} {t('BILLING.CREDITS_REMAINING', 'remaining')}
            </span>
          )}
          {monthlyGrant !== undefined && monthlyGrant !== 0 && (
            <span className="text-xs text-muted-foreground">
              · {formatGrant(monthlyGrant)} {t('BILLING.CREDITS_GRANTED', 'granted / month')}
            </span>
          )}
        </div>
      )}

      {featureOverrideKeys.length > 0 && (
        <p className="mt-4 text-sm text-muted-foreground">
          {t('BILLING.CUSTOM_FEATURES', 'Custom features:')}{' '}
          {featureOverrideKeys
            .map((k) => {
              const v = data.plan?.features?.[k];
              const val = typeof v === 'boolean' ? t(v ? 'BILLING.FEATURE_ON' : 'BILLING.FEATURE_OFF', v ? 'on' : 'off') : formatFeatureValue(v);
              return `${FEATURE_LABELS[k] || k}: ${val}`;
            })
            .join(', ')}
        </p>
      )}
    </div>
  );
};
